export type WarehousePreparationViewTask = {
  status: string;
  dueAt: string | null;
  createdAt: string;
};

export type WarehousePreparationOwnedTask = WarehousePreparationViewTask & {
  routeLeg: string;
  targetWarehouseCode: string | null;
  stagingWarehouseCode: string;
};

export function warehousePreparationOwnerRole(
  task: WarehousePreparationOwnedTask,
) {
  if (
    task.routeLeg === "SHORTAGE_REVIEW" ||
    task.routeLeg === "DATA_QUALITY_REVIEW" ||
    task.routeLeg === "OFF_ROUTE_STOCK_REVIEW"
  )
    return "PLANLAMA" as const;
  if (
    task.routeLeg === "VERIFY_STAGING" ||
    (task.routeLeg === "OPEN_TRANSFER_MONITOR" &&
      task.targetWarehouseCode === task.stagingWarehouseCode)
  )
    return "CEP_DEPO_SORUMLUSU" as const;
  return "DEPO_GOREVLISI" as const;
}

export function visibleWarehousePreparationTasks<
  T extends WarehousePreparationOwnedTask,
>(tasks: T[], role: string) {
  if (role !== "DEPO_GOREVLISI" && role !== "CEP_DEPO_SORUMLUSU")
    return tasks;
  return tasks.filter((task) => warehousePreparationOwnerRole(task) === role);
}

export type WarehousePreparationBatchTask = {
  materialCode: string;
  unit: string;
  routeLeg: string;
  sourceWarehouseCode: string | null;
  targetWarehouseCode: string | null;
  stagingWarehouseCode: string;
  dueAt: string | null;
  requestedQuantity: number;
  workOrderNo: string;
};

export type PreparationNotificationTarget = {
  taskId: string | null;
  checkId: string | null;
  eventName: string;
};

export function preparationNotificationAction(
  notification: PreparationNotificationTarget,
) {
  if (notification.taskId)
    return {
      label: "Göreve git",
      targetId: `warehouse-task-${notification.taskId}`,
    };
  if (
    notification.checkId &&
    notification.eventName !== "material_preparation.ready"
  )
    return {
      label: "Kontrole git",
      targetId: `material-preparation-check-${notification.checkId}`,
    };
  return { label: "Okundu işaretle", targetId: null };
}

const terminalStatuses = new Set(["READY", "SUPERSEDED", "EXCEPTION_CLOSED"]);
const criticalStatuses = new Set([
  "STOCK_SHORTAGE",
  "SYSTEM_PHYSICAL_VARIANCE",
  "INVALID_MATERIAL",
  "BLOCKED",
  "UNRESOLVED",
  "ESCALATION_REQUIRED",
]);
const confirmationStatuses = new Set([
  "DISPATCHED",
  "STAGING_CONFIRMATION_PENDING",
  "PHYSICAL_CONFIRMED",
]);

function istanbulDateKey(value: Date) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(value);
}

export function groupWarehousePreparationTasks<
  T extends WarehousePreparationViewTask,
>(tasks: T[], now = new Date()) {
  const today = istanbulDateKey(now);
  const active = tasks.filter((task) => !terminalStatuses.has(task.status));
  const critical = active.filter(
    (task) =>
      criticalStatuses.has(task.status) ||
      (task.dueAt !== null && new Date(task.dueAt).getTime() < now.getTime()),
  );
  const criticalSet = new Set(critical);
  const confirmation = active.filter(
    (task) => !criticalSet.has(task) && confirmationStatuses.has(task.status),
  );
  const confirmationSet = new Set(confirmation);
  const remaining = active.filter(
    (task) => !criticalSet.has(task) && !confirmationSet.has(task),
  );
  const work = remaining.filter(
    (task) =>
      task.dueAt === null ||
      istanbulDateKey(new Date(task.dueAt)) === today ||
      istanbulDateKey(new Date(task.createdAt)) === today,
  );
  const workSet = new Set(work);
  const upcoming = remaining.filter((task) => !workSet.has(task));

  return [
    {
      key: "critical",
      title: "Gecikmiş veya kritik görevler",
      items: critical,
    },
    { key: "work", title: "Bugün hazırlanacak işler", items: work },
    { key: "upcoming", title: "Yaklaşan üretimler", items: upcoming },
    {
      key: "confirmation",
      title: "Fiziki teyit bekleyenler",
      items: confirmation,
    },
    {
      key: "ready",
      title: "Tamamlanan hazırlıklar",
      items: tasks.filter((task) =>
        ["READY", "EXCEPTION_CLOSED"].includes(task.status),
      ),
    },
  ] as const;
}

export function groupWarehousePreparationTaskBatches<
  T extends WarehousePreparationBatchTask,
>(tasks: T[]) {
  const batches = new Map<string, T[]>();
  for (const task of tasks) {
    const day = task.dueAt
      ? istanbulDateKey(new Date(task.dueAt))
      : "unscheduled";
    const key = [
      task.materialCode,
      task.unit,
      task.routeLeg,
      task.sourceWarehouseCode,
      task.targetWarehouseCode,
      task.stagingWarehouseCode,
      day,
    ].join("|");
    batches.set(key, [...(batches.get(key) ?? []), task]);
  }
  return [...batches.entries()].map(([key, items]) => ({
    key,
    tasks: items,
    totalQuantity: items.reduce(
      (total, item) => total + item.requestedQuantity,
      0,
    ),
    workOrders: [...new Set(items.map((item) => item.workOrderNo))].join(", "),
  }));
}
